import { useMemo } from 'react';
import { getEffectiveRules } from '@hubertai/screening-question-types';
import type { TQuestionRule as TSharedQuestionRule } from '@hubertai/screening-question-types';
import { useQuestion } from './useQuestion';
import { useTemplateId } from './useTemplate';
import type { TQuestionRule } from 'types/rules.types';
import { useAppSelector } from 'rtk/query/hooks';

export type TUseQuestionRules = {
  list: TQuestionRule[];
  isDefault: boolean;
};

// TQuestionRule here is structurally identical to (but a nominally distinct type from) the
// shared-package one getEffectiveRules uses — safe to cast, not safe to drop the cast.
export function getNodeRules(
  nodeParameters: ({ rules?: TQuestionRule[] } & Record<string, unknown>) | undefined,
  questionDefaultRules: TQuestionRule[] | undefined,
): TUseQuestionRules {
  return getEffectiveRules(
    nodeParameters as unknown as { rules?: TSharedQuestionRule[] } | undefined,
    questionDefaultRules as unknown as TSharedQuestionRule[] | undefined,
  ) as unknown as TUseQuestionRules;
}

// A freshly copied question (InTemplateCopy) is edited under a brand-new id that was never
// fetched into the Library, so questionConfig.rules never resolves for it even though templateId
// is set — fall back to the local editor's rules (already seeded with the copied rule set) rather
// than treating "inside a template" as "always trust the Library".
export function selectQuestionDefaultRules(
  templateId: string | null | undefined,
  questionConfigRules: TQuestionRule[] | undefined,
  localRules: TQuestionRule[] | undefined,
): TQuestionRule[] | undefined {
  if (!templateId) return localRules;
  return questionConfigRules ?? localRules;
}

export function useQuestionRules(nodeId: string): TUseQuestionRules {
  const { questionConfig } = useQuestion(nodeId);
  const templateId = useTemplateId();
  const parameters = useAppSelector((state) => state.Template?.present?.c?.parameters);
  // Outside a template, questionConfig.rules (Library-cached) only refreshes on a server round
  // trip — the local editor's pending rules (kept live by upsertQuestionRule) are the freshest
  // source for a question being edited standalone.
  const localRules = useAppSelector((state) => state.questionEditor.byId[nodeId]?.rules);

  // Computed directly rather than mirrored into useState via an effect: that pattern lags the
  // returned value by one render cycle behind `parameters`/`localRules`, which a caller whose own
  // value prop round-trips through a commit-then-rerender (e.g. ComboboxMultiple's value/selected
  // sync effect) can observe as a stale value being live — and re-report as if it were a fresh,
  // intentional change. See useQuestionRules.languageCondition.integration.browser.spec.tsx.
  return useMemo<TUseQuestionRules>(() => {
    if (!nodeId) {
      return { list: [], isDefault: true };
    }

    // Outside a template, there's no override to read — parameters is template-scoped, so pass
    // undefined and let getNodeRules fall back to the question's own default rules.
    const nodeParameters = templateId ? parameters?.[nodeId] : undefined;

    // questionConfig.rules is typed against the shared-package TQuestionRule, structurally
    // identical to (but a nominally distinct type from) the frontend-local one getNodeRules uses.
    const questionDefaultRules = selectQuestionDefaultRules(
      templateId,
      questionConfig?.rules as TQuestionRule[] | undefined,
      localRules,
    );
    return getNodeRules(nodeParameters, questionDefaultRules);
    // language itself isn't read here, but questionConfig is already recomputed per active
    // language (useQuestion applies it via applyTemplateOptionsToQuestion), so a language switch
    // still triggers a recompute through that dependency.
  }, [questionConfig, parameters, nodeId, templateId, localRules]);
}
